import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import './EmployeeDetailsModal.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaEdit } from "react-icons/fa";

function EmployeeDetailsModal({ employee, onClose }) {
  const [overtimeData, setOvertimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    days_remaining: null,
    expiration_date: null
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchOvertimeData();
    getUserRole();
  }, []);

  // Function to get the user's role
  const getUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('wsp_email', user.email)
          .single();

        if (error) throw error;
        setUserRole(data.role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error.message);
    }
  };

  const fetchOvertimeData = async () => {
    try {
      // First try to fetch existing overtime data
      let { data, error } = await supabase
        .from('overtime_vacation_days')
        .select('*')
        .eq('employee_id', employee.id)
        .single();

      if (error && error.code === 'PGRST116') { // No data found
        // Create new record with null values
        const { data: newData, error: insertError } = await supabase
          .from('overtime_vacation_days')
          .insert([
            {
              employee_id: employee.id,
              days_remaining: null,
              expiration_date: null
            }
          ])
          .select()
          .single();

        if (insertError) throw insertError;
        data = newData;
      } else if (error) {
        throw error;
      }

      setOvertimeData(data);
      setEditFormData({
        days_remaining: data?.days_remaining || null,
        expiration_date: data?.expiration_date || null
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit mode toggle
  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    // Reset form data to original values
    setEditFormData({
      days_remaining: overtimeData?.days_remaining || null,
      expiration_date: overtimeData?.expiration_date || null
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // Handle date picker change
  const handleDateChange = (date) => {
    if (date) {
      // Fix: Adjust the date to maintain the correct day regardless of timezone
      const adjustedDate = new Date(date);
      adjustedDate.setHours(12); // Set to noon to avoid timezone issues

      setEditFormData({
        ...editFormData,
        expiration_date: adjustedDate.toISOString().split('T')[0]
      });
    } else {
      setEditFormData({
        ...editFormData,
        expiration_date: null
      });
    }
  };

  const isPastDay = (date) => {
    // Return true if the date is in the past (before today)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
    return date < today;
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      // Validate the data
      if (editFormData.days_remaining && editFormData.days_remaining < 0) {
        throw new Error('Los días disponibles no pueden ser negativos');
      }

      const { error } = await supabase
        .from('overtime_vacation_days')
        .update({
          days_remaining: editFormData.days_remaining,
          expiration_date: editFormData.expiration_date
        })
        .eq('id', overtimeData.id);

      if (error) throw error;

      // Update local state with new data
      setOvertimeData({
        ...overtimeData,
        days_remaining: editFormData.days_remaining,
        expiration_date: editFormData.expiration_date
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving overtime data:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Calculate Seniority 
  const calculateSeniority = (dateOfEntry) => {
    if (!dateOfEntry) return '0 años';

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDate = new Date(todayStr);

    const entryStr = dateOfEntry.split('T')[0];
    const entryDate = new Date(entryStr);

    let years = todayDate.getFullYear() - entryDate.getFullYear();

    const anniversaryDate = new Date(
      todayDate.getFullYear(),
      entryDate.getMonth(),
      entryDate.getDate()
    );

    if (todayDate < anniversaryDate) {
      years--;
    }

    return `${years} años`;
  };

  // Calculate vacation days based of Mexican Labor Law
  const calculateVacationDays = (dateOfEntry) => {
    if (!dateOfEntry) return 0;
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDate = new Date(todayStr);

    const entryStr = dateOfEntry.split('T')[0];
    const entryDate = new Date(entryStr);

    let years = todayDate.getFullYear() - entryDate.getFullYear();

    const anniversaryDate = new Date(
      todayDate.getFullYear(),
      entryDate.getMonth(),
      entryDate.getDate()
    );

    if (todayDate < anniversaryDate) {
      years--;
    }

    if (years <= 0) return 0;
    else if (years === 1) return 12;
    else if (years === 2) return 14;
    else if (years === 3) return 16;
    else if (years === 4) return 18;
    else if (years === 5) return 20;
    else if (years >= 6 && years <= 10) return 22;
    else if (years >= 11 && years <= 15) return 24;
    else if (years >= 16 && years <= 20) return 26;
    else if (years >= 21 && years <= 25) return 28;
    else if (years >= 26 && years <= 30) return 30;
    else return 32;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'NO TIENE';
    // Create date at noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('es-MX');
  };

  // Handle no tiene prórroga button
  const handleNoTieneProrroga = async () => {
    setSaving(true);
    setError('');

    try {
      // Update database with NULL values for both fields
      const { error } = await supabase
        .from('overtime_vacation_days')
        .update({
          days_remaining: null,
          expiration_date: null
        })
        .eq('id', overtimeData.id);

      if (error) throw error;

      // Update local state with NULL values
      setOvertimeData({
        ...overtimeData,
        days_remaining: null,
        expiration_date: null
      });

      // Also update form data to maintain consistency
      setEditFormData({
        days_remaining: null,
        expiration_date: null
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error resetting overtime data:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Render the edit form for overtime data
  const renderOvertimeEditForm = () => (
    <div className="overtime-edit-form">
      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>Días disponibles que pertenecen a prórroga:</label>
        <input
          type="number"
          name="days_remaining"
          value={editFormData.days_remaining || ''}
          onChange={handleInputChange}
          placeholder="Ingrese cantidad de días"
        />
      </div>

      <div className="form-group">
        <label>Fecha de expiración:</label>
        <DatePicker
          selected={editFormData.expiration_date ? new Date(editFormData.expiration_date + 'T12:00:00') : null}
          onChange={handleDateChange}
          dateFormat="dd/MM/yyyy"
          placeholderText="Seleccione fecha"
          className="date-picker-input"
          filterDate={(date) => {
            // Filter out past days and days on or after renovation_day
            const isPast = isPastDay(date);
            
            // Check if date is on or after renovation_day
            let isOnOrBeforeRenovation = false;
            if (employee.renovation_day) {
              // Create a date object for renovation_day (set to noon to avoid timezone issues)
              const renovationDate = new Date(employee.renovation_day + 'T12:00:00');
              
              // Set both dates to midnight for fair comparison
              renovationDate.setHours(0, 0, 0, 0);
              const dateToCheck = new Date(date);
              dateToCheck.setHours(0, 0, 0, 0);
              
              // Compare the selected date with renovation date (must be strictly before)
              isOnOrBeforeRenovation = dateToCheck < renovationDate;
            } else {
              // If there's no renovation day, any future date is valid
              isOnOrBeforeRenovation = true;
            }
            
            // Return true only if the date is not in the past AND is before renovation day
            return !isPast && isOnOrBeforeRenovation;
          }}
          calendarClassName="weekend-enabled"
        />
      </div>

      <div className="modal-buttons">
        <button className="save-button" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
        <button className="no-tiene-prorroga-button" onClick={handleNoTieneProrroga} disabled={saving}>
          No Tiene Prórroga
        </button>
        <button className="cancel-button" onClick={handleCancelEdit} disabled={saving}>
          Cancelar
        </button>
      </div>
    </div>
  );

  // Render the display view for overtime data
  const renderOvertimeDisplay = () => (
    <>
      <div className="detail-row">
        <span className="detail-label">Días disponibles que pertenecen a prórroga:</span>
        <span className="detail-value">
          {overtimeData?.days_remaining ? `${overtimeData.days_remaining} días` : 'NO TIENE'}
        </span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Fecha de expiración (dd/mm/yyyy):</span>
        <span className="detail-value">{formatDate(overtimeData?.expiration_date)}</span>
      </div>
      
      {/* Only show edit button for admin or RH */}
      {(userRole === 'admin' || userRole === 'RH') && (
        <div className="detail-row edit-icon-row">
          <button className="icon-button edit-button" title="Editar días de prórroga" onClick={handleEditClick}>
            <FaEdit className="edit-icon" />
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="employee-details-modal">
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="close-button" onClick={onClose}>×</button>
          <h2>Detalles del empleado</h2>
          <div className="employee-details">
            <div className="detail-row">
              <span className="detail-label">Fecha de ingreso:</span>
              <span className="detail-value">{formatDate(employee.date_of_entry)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Área:</span>
              <span className="detail-value">{employee.area || 'No especificada'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Antigüedad:</span>
              <span className="detail-value">{calculateSeniority(employee.date_of_entry)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Días por ley:</span>
              <span className="detail-value">{calculateVacationDays(employee.date_of_entry)} días</span>
            </div>

            {/* Overtime vacation days section */}
            <div className="section-divider"></div>
            <h2>Días extra de vacaciones por prórroga</h2>

            {loading ? (
              <div className="loading">Cargando...</div>
            ) : (
              isEditing ? renderOvertimeEditForm() : renderOvertimeDisplay()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetailsModal;